import type { InMemoryCache, NormalizedCacheObject } from '@apollo/client'

const STORAGE_KEY = 'communo:apollo-cache'
// Bump when the cache SHAPE changes incompatibly (typePolicies, fragment fields,
// normalized key fields) so an old snapshot is discarded instead of restored
// into a new client that reads different fields.
const SCHEMA_VERSION = 1
// Ignore snapshots older than this so a long-lived tab does not repaint
// stale data (bookings/availability) as if it were fresh.
const MAX_AGE_MS = 24 * 60 * 60 * 1000
// Skip persisting if the cache serializes past this — keeps writes cheap and
// well under the sessionStorage quota.
const MAX_SERIALIZED_BYTES = 2_000_000
// Don't re-serialize the whole cache on every hide; at most once per window
// (rapid tab/app switching is common, especially on mobile).
const MIN_PERSIST_INTERVAL_MS = 5_000

type Envelope = { v: number; at: number; data: NormalizedCacheObject }

/**
 * Lightweight Apollo cache persistence to sessionStorage (navigation perf).
 *
 * sessionStorage — NOT localStorage — on purpose: the cache holds user data, so
 * persisting it across browser sessions on a shared device would leak it.
 * sessionStorage survives reloads and in-tab navigations (the instant-on-reload
 * win) but is cleared when the tab closes, and is purged on logout
 * (clearPersistedCache). Snapshots carry a schema version + timestamp so a
 * deploy that changes the cache shape, or a stale day-old snapshot, is dropped
 * rather than restored. Auth tokens live in OIDC storage, not the Apollo cache.
 */
export function restorePersistedCache(cache: InMemoryCache): void {
  if (typeof window === 'undefined') return
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return
    const env = JSON.parse(raw) as Partial<Envelope>
    if (
      env.v !== SCHEMA_VERSION ||
      typeof env.at !== 'number' ||
      Date.now() - env.at > MAX_AGE_MS ||
      !env.data
    ) {
      clearPersistedCache()
      return
    }
    cache.restore(env.data)
  } catch {
    // Corrupt/unavailable storage: start clean.
    clearPersistedCache()
  }
}

export function installCachePersist(cache: InMemoryCache): void {
  if (typeof window === 'undefined') return

  let lastPersistAt = 0
  const persist = (force: boolean) => {
    if (!force && Date.now() - lastPersistAt < MIN_PERSIST_INTERVAL_MS) return
    try {
      const envelope: Envelope = {
        v: SCHEMA_VERSION,
        at: Date.now(),
        data: cache.extract(),
      }
      const serialized = JSON.stringify(envelope)
      if (serialized.length > MAX_SERIALIZED_BYTES) {
        window.sessionStorage.removeItem(STORAGE_KEY)
        return
      }
      window.sessionStorage.setItem(STORAGE_KEY, serialized)
      lastPersistAt = Date.now()
    } catch {
      // Quota/serialization failure: cache simply stays in memory only.
    }
  }

  // `pagehide` is the last reliable chance to snapshot → always persist.
  window.addEventListener('pagehide', () => persist(true))
  // Tab/app switches fire often → throttle these.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') persist(false)
  })
}

/** Purge the persisted snapshot — call on logout so no user data lingers. */
export function clearPersistedCache(): void {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    // ignore
  }
}
